import { assert, ColorString, fix180LineInPlace, fix180Longitude, getAltitudeColor } from '@fe/core';
import * as turf from '@turf/turf';
import { Feature, LineString, Point, Position } from 'geojson';
import { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import { LngLatArray } from '../flights/types';
import { DEFAULT_ROUTE_FLOWN_COLOR, DEFAULT_ROUTE_WIDTH, type FeatureIds } from './routes-layer';
import { CatmullRom as catmullRomSegments, cubicSpline as cubicSplineSegments } from './smoothing';
import { FlownRoute, FlownRouteColorSettings, FlownRouteParams, FlownRouteSettings, RouteFlownPoint } from './types';

export const SOURCE = 'fe-routes-flown-source';
export const LINE = 'fe-route-flown-line';
export const LABELS = 'fe-route-flown-labels';
export const POINTS = 'fe-route-flown-line-pts';

const FLIGHT_PLAN_GREAT_CIRCLE_MIN_SEGMENT_DEG = 1.0;

type RouteSegment = {
    line: LngLatArray[];
    altitude: number | null;
    speed: number | null;
    fob: number | null;
    feedId: number | null;
    time: number | null;
};

export interface RouteFlownPointFeatureProperties {
    time: number | null;
    altitude: number | null;
    feedId: number | null;
    speed: number | null;
    tag: string;
    isProjected: boolean;
    color: number;
}

interface SegmentFeatureProperty {
    altitudeColor: string;
}

export class FlownRouteHelper {
    private readonly featureIds: Map<number, FeatureIds>;
    private map: MaplibreMap | null = null;
    private routes: Map<number, FlownRoute> = new Map();
    private _shouldSmoothen: boolean = true;
    private _showProjectedPoints: boolean = true;
    private _colorSettings: FlownRouteColorSettings | null = null;
    private _settings: FlownRouteSettings | null = null;
    private _params: FlownRouteParams = {
        algorithm: 'catmull-rom',
        numberOfSegments: 6,
        alpha: 0.5
    };

    constructor(
        featureIds: Map<number, FeatureIds>,
        private readonly beforeId?: string
    ) {
        this.featureIds = featureIds;
    }

    get settings(): FlownRouteSettings | null {
        return this._settings;
    }

    set settings(value: FlownRouteSettings | null) {
        this._settings = value;
        this.showHide();
    }

    get params() {
        return this._params;
    }

    set params(value: FlownRouteParams) {
        this._params = value;
        this.recreateAllFeatures();
    }

    get shouldSmoothen(): boolean {
        return this._shouldSmoothen;
    }

    set shouldSmoothen(value: boolean) {
        this._shouldSmoothen = value;
        this.recreateAllFeatures();
    }

    get showProjectedPoints(): boolean {
        return this._showProjectedPoints;
    }

    set showProjectedPoints(value: boolean) {
        this._showProjectedPoints = value;
        this.updateMapSettings();
        this.recreateAllFeatures();
    }

    get colorSettings(): FlownRouteColorSettings | null {
        return this._colorSettings;
    }

    set colorSettings(value: FlownRouteColorSettings) {
        this._colorSettings = value;
        this.updateMapSettings();
    }

    addToMap(map: MaplibreMap) {
        this.map = map;
        assert(this.map);
        const { shouldColorize = false, lineColor: color = DEFAULT_ROUTE_FLOWN_COLOR } = this._colorSettings || {};

        this.map.addSource(SOURCE, {
            type: 'geojson',
            data: turf.featureCollection([]),
            tolerance: 0
        });

        this.map.addLayer(
            {
                id: LINE,
                type: 'line',
                filter: ['==', ['geometry-type'], 'LineString'],
                source: SOURCE,
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-width': DEFAULT_ROUTE_WIDTH,
                    'line-color': shouldColorize ? ['get', 'altitudeColor'] : (color as string)
                }
            },
            this.beforeId
        );

        this.map.addLayer(
            {
                id: POINTS,
                type: 'circle',
                filter: ['==', ['geometry-type'], 'Point'],
                source: SOURCE,
                minzoom: 4,
                paint: {
                    'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 2, 7, 3, 10, 4],
                    'circle-stroke-color': 'rgb(255,255,255)'
                }
            },
            this.beforeId
        );
        this.map.addLayer(
            {
                id: LABELS,
                type: 'symbol',
                source: SOURCE,
                minzoom: 4,
                filter: ['==', ['geometry-type'], 'Point'],
                layout: {
                    'text-font': ['Open Sans Regular'],
                    'text-size': 10,
                    'text-field': ['get', 'tag'],
                    'text-anchor': 'top-left',
                    'icon-allow-overlap': true,
                    'text-offset': [0.0, 0.8],
                    'symbol-placement': 'point'
                },
                paint: {
                    'text-color': ['global-state', 'route-text-color']
                }
            },
            this.beforeId
        );

        this.updateMapSettings();
        this.showHide();
    }

    removeFromMap() {
        assert(this.map);

        this.map.removeLayer(LINE);
        this.map.removeLayer(POINTS);
        this.map.removeLayer(LABELS);

        this.map.removeSource(SOURCE);

        this.map = null;

        this.routes.clear();
    }

    remove(flightId: number) {
        assert(this.map);

        const source = this.map.getSource(SOURCE) as GeoJSONSource;
        source.updateData({ remove: this.featureIds.get(flightId)!.flown });

        this.routes.delete(flightId);
    }

    removeAll() {
        const source = this.map?.getSource(SOURCE) as GeoJSONSource;
        source?.updateData({ removeAll: true });

        this.routes.clear();
    }

    upsert(flightId: number, route: FlownRoute): GeoJSON.Feature[] {
        const source = this.map!.getSource(SOURCE) as GeoJSONSource;
        assert(source);

        const toRemove = this.featureIds.get(flightId)?.flown ?? [];
        const filtered = this.showProjectedPoints ? route : route.filter((x) => !isProjected(x));
        const segments = this.toSegments(filtered);
        const pointsFeatures = filtered.map((x, i) => this.createPointFeature(`${flightId}-flown-${i}-p`, x));
        const segmentsFeatures = this.createLineFeatures(flightId, segments);
        const features = [...pointsFeatures, ...segmentsFeatures];

        source.updateData({ add: features, remove: toRemove });
        this.routes.set(flightId, route);

        return features;
    }

    addRoutePoint(flightId: number, point: RouteFlownPoint, atIndex: number) {
        const source = this.map?.getSource(SOURCE) as GeoJSONSource;
        assert(source);

        const route = this.routes.get(flightId);
        if (!route) {
            return;
        }

        // Insert at the right spot
        route.splice(atIndex, 0, point);

        if (!this.showProjectedPoints && isProjected(point)) {
            return;
        }

        this.recreateFeature(flightId, route!);
    }

    getLastFlownPoint(flightId: number): RouteFlownPoint | null {
        const route = this.routes.get(flightId);
        return route?.at(-1) ?? null;
    }

    private recreateAllFeatures(): void {
        for (const [id, info] of this.routes) {
            this.recreateFeature(id, info);
        }
    }

    private recreateFeature(flightId: number, info: FlownRoute) {
        const features = this.upsert(flightId, info);
        const flightFeaturesIds = this.featureIds.get(flightId)!;
        flightFeaturesIds.flown = features.map((x) => x.id as string);
    }

    private showHide() {
        if (!this.map || !this.settings) {
            return;
        }

        const { show, showPoints, showLabels } = this.settings;

        this.map.setLayoutProperty(LINE, 'visibility', show ? 'visible' : 'none');
        this.map.setLayoutProperty(POINTS, 'visibility', show && showPoints ? 'visible' : 'none');
        this.map.setLayoutProperty(LABELS, 'visibility', show && showPoints && showLabels ? 'visible' : 'none');
    }

    private createPointFeature(featureId: string, point: RouteFlownPoint): Feature<Point, RouteFlownPointFeatureProperties> {
        return turf.point(
            point.position,
            {
                time: point.time,
                tag: getRouteFlownTag(point),
                isProjected: isProjected(point),
                color: toColorIndex(point),
                speed: point.speed,
                altitude: point.altitude,
                feedId: point.feedId
            } satisfies RouteFlownPointFeatureProperties,
            { id: featureId }
        );
    }

    private createLineFeatures(id: number, segments: RouteSegment[]): Feature<LineString, SegmentFeatureProperty>[] {
        const features: Feature<LineString, SegmentFeatureProperty>[] = [];
        // If we fail to find a color for this segment use the previous non null color
        let previousColor: ColorString | null = null;

        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            const color = segment.altitude !== null ? getAltitudeColor(segment.altitude / 100) : null;
            const segmentFinal = this.addGreatCircle(segment.line);
            // Normalize latitude so that we don't get lines across the screen
            fix180LineInPlace(segmentFinal);
            features.push(
                turf.lineString(
                    segmentFinal,
                    { altitudeColor: color ?? previousColor ?? '#000' },
                    {
                        id: `${id}-flown-${i}-l`
                    }
                )
            );

            if (color) {
                previousColor = color;
            }
        }

        for (const feature of features) {
            // Normalize all segments, otherwise we'll get horizontal lines across the screen
            fix180LineInPlace(feature.geometry.coordinates);
        }

        return features;
    }

    private addGreatCircle(segments: Position[]): Position[] {
        const line: Position[] = [];

        for (let i = 0; i < segments.length - 1; i++) {
            const current = segments[i];
            const next = segments[i + 1];

            // Simplified to prevent performance drop. If coords diff is smaller than given do not add GC points
            if (
                Math.abs(next[0] - current[0]) < FLIGHT_PLAN_GREAT_CIRCLE_MIN_SEGMENT_DEG &&
                Math.abs(next[1] - current[1]) < FLIGHT_PLAN_GREAT_CIRCLE_MIN_SEGMENT_DEG
            ) {
                line.push(current, next);
                continue;
            }

            const distance = turf.distance(current, next, { units: 'nauticalmiles' });
            const gcLine = turf.greatCircle(current, next, { npoints: Math.ceil(distance / 50) });
            if (gcLine.geometry.type === 'LineString') {
                line.push(...gcLine.geometry.coordinates);
            } else {
                for (const c of gcLine.geometry.coordinates) {
                    line.push(...c);
                }
            }
        }
        return line;
    }

    private updateMapSettings() {
        this.map?.setFilter(
            POINTS,
            this.showProjectedPoints
                ? ['==', ['geometry-type'], 'Point']
                : ['all', ['!', ['get', 'isProjected']], ['==', ['geometry-type'], 'Point']]
        );
        if (this.colorSettings) {
            this.map?.setPaintProperty(
                LINE,
                'line-color',
                this.colorSettings.shouldColorize ? ['get', 'altitudeColor'] : this.colorSettings.lineColor
            );
            const flownPoints = this.colorSettings.flownPoints;
            this.map?.setPaintProperty(POINTS, 'circle-color', [
                'match',
                ['get', 'color'],
                0,
                flownPoints.defaultColor,
                1,
                flownPoints.projectedColor,
                2,
                flownPoints.acarsColor,
                3,
                flownPoints.adsbTerrestrialColor,
                4,
                flownPoints.adsbSatelliteColor,
                5,
                flownPoints.faaAnspColor,
                flownPoints.defaultColor
            ]);
        }
    }

    private toSegments(route: FlownRoute): RouteSegment[] {
        if (this.shouldSmoothen) {
            const smoothed = smoothRoute(route, this.params);
            return smoothed.map(
                (x, i) =>
                    ({
                        line: x,
                        altitude: route[i].altitude,
                        speed: route[i].speed,
                        fob: route[i].fob,
                        feedId: route[i].feedId,
                        time: route[i].time
                    }) satisfies RouteSegment
            );
        }

        // Not smoothed path
        const result = [] as RouteSegment[];
        for (let i = 0; i < route.length - 1; i++) {
            result.push({
                line: [route[i].position, [fix180Longitude(route[i].position[0], route[i + 1].position[0]), route[i + 1].position[1]]],
                altitude: route[i].altitude,
                speed: route[i].speed,
                fob: route[i].fob,
                feedId: route[i].feedId,
                time: route[i].time
            });
        }
        return result;
    }
}

function smoothRoute(route: FlownRoute, params: FlownRouteParams): LngLatArray[][] {
    if (import.meta.env.DEV) {
        console.time('smoothing');
    }

    const result =
        params.algorithm === 'cubic-spline'
            ? cubicSplineSegments(
                  route.map((x) => x.position),
                  params.numberOfSegments,
                  0.6
              )
            : catmullRomSegments(
                  route.map((x) => x.position),
                  params.numberOfSegments,
                  params.alpha,
                  0.6
              );

    if (import.meta.env.DEV) {
        console.timeEnd('smoothing');
    }
    return result;
}

function getRouteFlownTag(item: RouteFlownPoint): string {
    return `${item.speed ?? '-'}kt ${(item.altitude != null ? item.altitude.toLocaleString('en-US') : null) ?? '-'}ft`;
}

function isProjected(point: RouteFlownPoint) {
    return point.feedId === 999;
}

function toColorIndex(point: RouteFlownPoint): number {
    // Unknown, use default color
    if (point.feedId === null) {
        return 0;
    }

    // Projected
    if (point.feedId === 999) {
        return 1;
    }

    // ACARS/3PDI
    if (point.feedId >= 1000 && point.feedId !== 1031) {
        return 2;
    }

    // ADS-B Terrestrial
    if (point.feedId === 190 || point.feedId === 192 || point.feedId === 194 || point.feedId === 196 || point.feedId === 198) {
        return 3;
    }

    // ADS-B Satellite
    if (point.feedId === 191 || point.feedId === 193 || point.feedId === 195 || point.feedId === 197 || point.feedId === 199) {
        return 4;
    }

    // FAA/ANSP & all other
    // ASDE-X (200-299), Mexico (108), Canada (101), UK (102), NZ (105 & 106), ASA (1031).
    // Also default everything else to this color
    return 5;
}
